
import {
  Component, Prop, VModel, Vue, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import { FileTypeEnum, SupportTicketMessageDto, SupportTicketsClient } from '@/services/api-clients';

@Component
export default class SupportTicketSaveFileDialog extends Vue {
  @VModel({ type: Boolean }) dialog!: boolean;

  @Prop({ type: Object, required: true }) message!: SupportTicketMessageDto;

  fileTypeId: FileTypeEnum | null = null;

  isOtherType = false;

  saving = false;

  fileName = '';

  mounted() {
    this.useOriginalName();
  }

  @Watch('isOtherType')
  onIsOtherTypeChange(val: boolean) {
    if (!val) return;
    this.fileTypeId = null;
    this.useOriginalName();
  }

  // Employees tend to keep whatever the field shows, so picking a type puts its name in right away.
  @Watch('fileTypeId')
  onFileTypeIdChange(val: FileTypeEnum | null) {
    if (val) {
      this.useTypeName();
    } else {
      this.useOriginalName();
    }
  }

  get extension(): string {
    const name = this.message.attachmentFileName ?? '';
    const dotIndex = name.lastIndexOf('.');
    return dotIndex > 0 ? name.substring(dotIndex) : '';
  }

  useOriginalName() {
    const name = this.message.attachmentFileName ?? '';
    this.fileName = this.extension ? name.substring(0, name.length - this.extension.length) : name;
  }

  useTypeName() {
    const option = this.fileTypeOptions.find((x) => x.value === this.fileTypeId);
    if (!option) return;
    this.fileName = `${option.text.trim().replace(/\s+-\s+/g, '_').replace(/\s+/g, '_')}_${moment().format('YYYY_MM_DD')}`;
  }

  // Copied from DocumentsAndFilesFormPanel.vue, so both selects offer the same types.
  get fileTypeOptions() {
    return [
      { value: FileTypeEnum.LeaseAgreement, text: this.$t(this.$LF.LeaseAgreement) as string },
      { value: FileTypeEnum.LoanAgreement, text: this.$t(this.$LF.LoanAgreement) as string },
      { value: FileTypeEnum.TransferProtocol, text: this.$t(this.$LF.TransferProtocol) as string },
      { value: FileTypeEnum.ReceivingProtocol, text: this.$t(this.$LF.ReceivingProtocol) as string },
      { value: FileTypeEnum.PromissoryNote, text: this.$t(this.$LF.PromissoryNote) as string },
      { value: FileTypeEnum.DeclarationLackOfSII, text: this.$t(this.$LF.DeclarationLackOfSII) as string },
      { value: FileTypeEnum.ContractOfMandateDriver, text: this.$t(this.$LF.ContractOfMandateDriver) as string },
      { value: FileTypeEnum.ContractOfMandateCourier, text: this.$t(this.$LF.ContractOfMandateCourier) as string },
      { value: FileTypeEnum.StudentStatusConfirmation, text: this.$t(this.$LF.StudentStatusConfirmation) as string },
      { value: FileTypeEnum.GeneralProtocol, text: this.$t(this.$LF.GeneralProtocol) as string },
      { value: FileTypeEnum.CashRegisterReceipt, text: this.$t(this.$LF.CashRegisterReceipt) as string },
      { value: FileTypeEnum.CashRegisterDisbursement, text: this.$t(this.$LF.CashRegisterDisbursement) as string },
      { value: FileTypeEnum.FoodContainerLeaseAgreement, text: this.$t(this.$LF.FoodContainerLeaseAgreement) as string },
      { value: FileTypeEnum.B2BContract, text: this.$t(this.$LF.B2BContract) as string },
      { value: FileTypeEnum.B2BFloviContract, text: this.$t(this.$LF.B2BFloviContract) as string },
      { value: FileTypeEnum.IdFront, text: this.$t(this.$LF.IdFront) as string },
      { value: FileTypeEnum.IdBack, text: this.$t(this.$LF.IdBack) as string },
      { value: FileTypeEnum.DriverLicenceFront, text: this.$t(this.$LF.DriverLicenceFront) as string },
      { value: FileTypeEnum.DriverLicenceBack, text: this.$t(this.$LF.DriverLicenceBack) as string },
      { value: FileTypeEnum.ProfilePhoto, text: this.$t(this.$LF.ProfilePhoto) as string },
      { value: FileTypeEnum.MedicalExamination, text: this.$t(this.$LF.MedicalExamination) as string },
      { value: FileTypeEnum.PsychologicalExamination, text: this.$t(this.$LF.PsychologicalExamination) as string },
      { value: FileTypeEnum.CertificateOfNoCriminalRecord, text: this.$t(this.$LF.CertificateOfNoCriminalRecord) as string },
      { value: FileTypeEnum.BoltFoodContract, text: this.$t(this.$LF.BoltFoodContract) as string },
      { value: FileTypeEnum.CourierInstructions, text: this.$t(this.$LF.CourierInstructions) as string },
      { value: FileTypeEnum.ContractOfMandateOtherEmployer, text: this.$t(this.$LF.ContractOfMandateOtherEmployer) as string },
      { value: FileTypeEnum.WorkPermit, text: this.$t(this.$LF.WorkPermit) as string },
      { value: FileTypeEnum.AnnuitantCard, text: this.$t(this.$LF.AnnuitantCard) as string },
      { value: FileTypeEnum.PensionerCard, text: this.$t(this.$LF.PensionerCard) as string },
      { value: FileTypeEnum.DisabilityCertificate, text: this.$t(this.$LF.DisabilityCertificate) as string },
      { value: FileTypeEnum.InsuranceFromDuty, text: this.$t(this.$LF.InsuranceFromDuty) as string },
      { value: FileTypeEnum.StudentCard, text: this.$t(this.$LF.StudentCard) as string },
    ];
  }

  save() {
    this.saving = true;
    new SupportTicketsClient().saveTicketFileToDriverFiles({
      messageId: this.message.id,
      fileTypeId: this.isOtherType ? undefined : this.fileTypeId ?? undefined,
      fileName: this.fileName.trim(),
    }).then(({ success, errors }) => {
      if (!success) {
        this.$notifier.error(errors ?? []);
        return;
      }
      this.dialog = false;
    }).finally(() => {
      this.saving = false;
    });
  }
}
